<script setup>
import { ref } from 'vue';
import GlobalDashboard from '@/tabs/foreign/GlobalDashboard.vue';
import GlobalInvestingRecommender from '@/tabs/foreign/GlobalInvestingRecommender.vue';
import GiftCityWorkflow from '@/tabs/foreign/GiftCityWorkflow.vue';
import GiftCityUDRETF from '@/tabs/foreign/GiftCityUDRETF.vue';
import FeederFunds from '@/tabs/foreign/FeederFunds.vue';
import DirectEquity from '@/tabs/foreign/DirectEquity.vue';
import LRSA2FundTransferProcess from '@/tabs/foreign/LRSA2FundTransferProcess.vue';

const activeTab = ref('dashboard');
const tabContainer = ref(null);

const tabs = [
  { id: 'dashboard', label: 'Portfolio Dashboard', component: 'GlobalDashboard' },
  { id: 'recommender', label: 'Recommender', component: 'GlobalInvestingRecommender' },
  { id: 'giftCity', label: 'GIFT City (Cashflow)', component: 'GiftCityWorkflow' },
  { id: 'giftCityETF', label: 'GIFT City - Funds, ETFs & UDRs', component: 'GiftCityUDRETF' },
  { id: 'feederFunds', label: 'Feeder Funds', component: 'FeederFunds' },
  { id: 'directEquity', label: 'Direct Equity', component: 'DirectEquity' },
  { id: 'lrsA2Process', label: 'LRS A2 Process', component: 'LRSA2FundTransferProcess' }
];

const scrollTabs = (direction) => {
  if (tabContainer.value) {
    const scrollAmount = 200;
    const scrollLeft = direction === 'left'
      ? tabContainer.value.scrollLeft - scrollAmount
      : tabContainer.value.scrollLeft + scrollAmount;

    tabContainer.value.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });
  }
};
</script>

<template>
  <div class="foreign-investment-container">
    <!-- Tab Navigation with Scroll Buttons -->
    <div class="tab-navigation-wrapper">
      <button class="scroll-button left" @click="scrollTabs('left')" aria-label="Scroll left">
        <i class="pi pi-chevron-left"></i>
      </button>

      <div class="tab-navigation" ref="tabContainer">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          :class="['tab-button', { active: activeTab === tab.id }]"
          @click="activeTab = tab.id"
        >
          {{ tab.label }}
        </button>
      </div>

      <button class="scroll-button right" @click="scrollTabs('right')" aria-label="Scroll right">
        <i class="pi pi-chevron-right"></i>
      </button>
    </div>

    <!-- Tab Content -->
    <div class="tab-content">
      <GlobalDashboard v-if="activeTab === 'dashboard'" />
      <GlobalInvestingRecommender v-else-if="activeTab === 'recommender'" />
      <GiftCityWorkflow v-else-if="activeTab === 'giftCity'" />
      <GiftCityUDRETF v-else-if="activeTab === 'giftCityETF'" />
      <FeederFunds v-else-if="activeTab === 'feederFunds'" />
      <DirectEquity v-else-if="activeTab === 'directEquity'" />
      <LRSA2FundTransferProcess v-else-if="activeTab === 'lrsA2Process'" />
    </div>
  </div>
</template>

<style scoped>
.foreign-investment-container {
  width: 100%;
  padding: 0;
}

.tab-navigation-wrapper {
  position: relative;
  display: flex;
  align-items: stretch;
  background: white;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.scroll-button {
  flex-shrink: 0;
  width: 40px;
  border: none;
  background: linear-gradient(90deg, white 0%, rgba(255, 255, 255, 0.9) 100%);
  color: #6b7280;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-bottom: 2px solid #e5e7eb;
}

.scroll-button:hover {
  color: #3b82f6;
  background: linear-gradient(90deg, #f9fafb 0%, rgba(249, 250, 251, 0.9) 100%);
}

.scroll-button.left {
  border-radius: 12px 0 0 0;
  border-right: 1px solid #e5e7eb;
}

.scroll-button.right {
  border-radius: 0 12px 0 0;
  border-left: 1px solid #e5e7eb;
}

.scroll-button i {
  font-size: 1rem;
  font-weight: bold;
}

.tab-navigation {
  display: flex;
  gap: 4px;
  flex: 1;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 0;
  padding: 12px 16px 0;
  overflow-x: hidden;
  overflow-y: hidden;
  scroll-behavior: smooth;
}

.tab-button {
  padding: 10px 18px;
  border: none;
  background: transparent;
  color: #6b7280;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: all 0.3s ease;
  position: relative;
  white-space: nowrap;
  flex-shrink: 0;
}

.tab-button:hover {
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.05);
}

.tab-button.active {
  color: #3b82f6;
  border-bottom-color: #3b82f6;
  background: rgba(59, 130, 246, 0.08);
}

.tab-content {
  background: white;
  border-radius: 0 0 12px 12px;
  padding: 16px 24px 24px 24px;
  min-height: 500px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

@media (max-width: 768px) {
  .scroll-button {
    width: 32px;
  }

  .scroll-button i {
    font-size: 0.875rem;
  }

  .tab-navigation {
    padding: 10px 12px 0;
    gap: 2px;
  }

  .tab-button {
    padding: 8px 14px;
    font-size: 0.8rem;
  }

  .tab-content {
    padding: 12px 16px 16px 16px;
  }
}

@media (max-width: 1024px) {
  .tab-button {
    padding: 9px 16px;
    font-size: 0.85rem;
  }
}
</style>
